import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Info, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { fetchMatch } from '~/api/matches';
import { useMatchStore } from '~/game/matchStore';
import type { Phase } from '~/game/phases';
import { closeMatchSocket, connectMatch } from '~/game/socket';
import { getSession, useSession } from '~/lib/auth';
import { resolveHandle } from '~/lib/handle';
import { DailyRoom } from '~/screens/DailyRoom';
import { Lobby } from '~/screens/Lobby';
import { Results } from '~/screens/Results';
import { Submit } from '~/screens/Submit';
import { Vote } from '~/screens/Vote';
import { buildHead } from '~/seo/site';
import { MatchChat } from '~/ui/MatchChat';

type FlipSource = {
  id: string;
  label: string;
  url: string;
  durationSec: number | null;
};

export const Route = createFileRoute('/room/$code')({
  head: ({ params }) =>
    buildHead({
      title: `Match ${params.code}`,
      description: 'Live beat-battle match.',
      path: `/room/${params.code}`,
      noindex: true,
    }),
  component: Room,
});

function readUserParam(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const val = new URLSearchParams(window.location.search).get('user');
  return val?.trim() ? val.trim() : undefined;
}

const PHASES: Phase[] = ['lobby', 'submit', 'upload', 'vote', 'results'];
function toPhase(x: string | null | undefined): Phase {
  const v = (x ?? 'lobby') as Phase;
  return PHASES.includes(v) ? v : 'lobby';
}

function Room() {
  const { code } = Route.useParams();
  const phase = useMatchStore((s) => s.phase);
  const setCurrentUser = useMatchStore((s) => s.setCurrentUser);
  const setPhase = useMatchStore((s) => s.setPhase);
  const currentUser = useMatchStore((s) => s.currentUser);
  const queryClient = Route.useRouteContext().queryClient;
  const [flipSource, setFlipSource] = useState<FlipSource | null>(null);
  const { data: session } = useSession();
  const isAuthenticated = !!session;
  const isPaidOrAdmin = session?.plan === 'paid' || session?.role === 'admin';

  // Match data is owned by react-query so child screens (DailyRoom, etc.)
  // can invalidate ['match', code] after actions like /daily-challenge/enter
  // and the parent re-fetches. Earlier this was a useState fed once on
  // mount; invalidating did nothing and the kit never appeared after Enter.
  // currentUser is in the queryKey so signing in mid-room re-fetches with
  // the right caller context (mySubmission + samplePack gating).
  const matchQuery = useQuery({
    queryKey: ['match', code, currentUser],
    queryFn: () => fetchMatch(code, currentUser),
    staleTime: 10_000,
  });
  const matchData = matchQuery.data ?? null;

  const codeRef = useRef(code);
  const queryClientRef = useRef(queryClient);
  const setCurrentUserRef = useRef(setCurrentUser);
  const setPhaseRef = useRef(setPhase);

  useEffect(() => {
    // Authenticated users always use their persisted handle from the session
    // so their name shows up correctly in the roster and on the leaderboard.
    // Guests fall back to the ?user= URL override or a sessionStorage handle.
    let cancelled = false;

    const connect = (handle: string) => {
      setCurrentUserRef.current(handle);
      connectMatch(codeRef.current, queryClientRef.current, handle);
    };

    getSession()
      .then((session) => {
        if (cancelled) return;
        const handle = session?.handle ? session.handle : resolveHandle(readUserParam());
        connect(handle);
      })
      .catch(() => {
        if (cancelled) return;
        connect(resolveHandle(readUserParam()));
      });

    return () => {
      cancelled = true;
      closeMatchSocket();
    };
  }, []); // mount-only: refs carry the stable values

  // Hydrate phase + countdown from the server data when it lands. Without
  // this, a refresh during Submit/Reveal/Vote/Results bounces the user back
  // to Lobby because matchStore defaults to phase=lobby. Daily matches
  // bypass phase routing entirely.
  useEffect(() => {
    if (!matchData || matchData.mode === 'daily') return;
    const p = toPhase(matchData.currentPhase ?? matchData.status);
    setPhaseRef.current(p, matchData.transitionsAt ?? null);
    setFlipSource(matchData.flipSource ?? null);
  }, [matchData]);

  // Hold the layout until the first match fetch lands. Otherwise we briefly
  // render the regular Room/Lobby shell before flipping to DailyRoom, which
  // looks like the page is redirecting through several states.
  if (!matchData) {
    return (
      <section className="mx-auto flex min-h-[40vh] max-w-5xl items-center justify-center px-4 py-8 text-muted-foreground">
        <Loader2 className="size-5 animate-spin mr-2" />
        Loading room...
      </section>
    );
  }

  // Daily match: render the dedicated async layout (no phase routing).
  if (matchData.mode === 'daily') {
    return (
      <section className="mx-auto max-w-5xl px-4 py-8">
        <header className="flex items-baseline justify-between pb-4 border-b border-border mb-6">
          <h2 className="text-2xl font-bold">Daily Challenge</h2>
          <code className="font-mono text-primary text-xl tracking-widest">{code}</code>
        </header>
        <DailyRoom match={matchData} currentUser={currentUser} />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <header className="flex items-baseline justify-between pb-4 border-b border-border mb-6">
        <h2 className="text-2xl font-bold">Room</h2>
        <code className="font-mono text-primary text-xl tracking-widest">{code}</code>
      </header>
      {!isAuthenticated && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          <Info className="size-4 shrink-0 mt-0.5 text-amber-400" />
          <div className="flex-1 space-y-1">
            <div className="font-medium">Stay on this device until the match ends.</div>
            <div className="text-xs text-amber-200/85">
              Your guest identity is tied to this browser, so switching to another device mid-match
              will lose your submission. Signed-in producers don't have this problem - their matches
              sync across every device they use.
            </div>
          </div>
        </div>
      )}
      {flipSource && phase === 'submit' && (
        <div className="mb-6 rounded-lg border border-primary/40 bg-primary/5 p-4">
          <p className="text-xs uppercase tracking-wide text-primary">Flip this loop</p>
          <p className="mt-1 text-sm font-medium">{flipSource.label}</p>
          {/* biome-ignore lint/a11y/useMediaCaption: source loop */}
          <audio src={flipSource.url} controls className="mt-3 w-full" preload="metadata" />
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          {phase === 'lobby' && <Lobby code={code} />}
          {phase === 'submit' && <Submit code={code} />}
          {phase === 'upload' && <Submit code={code} uploadOnly />}
          {phase === 'vote' && <Vote code={code} />}
          {phase === 'results' && <Results code={code} />}
        </div>
        <MatchChat code={code} isAuthenticated={isAuthenticated} isPaidOrAdmin={isPaidOrAdmin} />
      </div>
    </section>
  );
}
